import { useState } from 'react';
import Layout from '../../components/feature/Layout';
import { campaigns, campaignCategories, type Campaign } from '../../mocks/campaigns';

const statusColors: Record<string, string> = {
  active: 'bg-green-50 text-green-600',
  inactive: 'bg-charcoal-100 text-charcoal-400',
};

function CampaignBadge({ label, color }: { label: string; color: string }) {
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${color}`}>{label}</span>;
}

export default function CampaignManagement() {
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('Tüm');
  const [status, setStatus] = useState('Tümü');
  const [filterPopular, setFilterPopular] = useState(false);
  const [filterTrending, setFilterTrending] = useState(false);
  const [filterNew, setFilterNew] = useState(false);

  const filtered = campaigns.filter(c => {
    const matchSearch = c.title.toLowerCase().includes(search.toLowerCase()) || c.slug.includes(search.toLowerCase());
    const matchCat = category === 'Tüm' || c.category === category;
    const matchStatus = status === 'Tümü' || (status === 'Aktif' ? c.status === 'active' : c.status === 'inactive');
    const matchPop = !filterPopular || c.popular;
    const matchTrend = !filterTrending || c.trending;
    const matchNew = !filterNew || c.isNew;
    return matchSearch && matchCat && matchStatus && matchPop && matchTrend && matchNew;
  });

  return (
    <Layout>
      {/* Filters */}
      <div className="bg-white rounded-xl2 shadow-card p-5 mb-5">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <div className="w-4 h-4 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2">
              <i className="ri-search-line text-charcoal-300 text-[14px]" />
            </div>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Kampanya ara..." className="w-full h-[38px] pl-9 pr-4 bg-[#FAF8F4] border border-border rounded-xl text-[13px] text-charcoal-500 placeholder-charcoal-300 focus:outline-none focus:border-champagne-400 transition-colors" />
          </div>
          {/* Category */}
          <select value={category} onChange={e => setCategory(e.target.value)} className="h-[38px] px-3 bg-[#FAF8F4] border border-border rounded-xl text-[13px] text-charcoal-500 focus:outline-none focus:border-champagne-400 cursor-pointer">
            <option value="Tüm">Tüm Kategoriler</option>
            {campaignCategories.map(c => <option key={c}>{c}</option>)}
          </select>
          {/* Status */}
          <div className="flex bg-[#FAF8F4] rounded-xl p-1 gap-1">
            {['Tümü', 'Aktif', 'Pasif'].map(s => (
              <button key={s} onClick={() => setStatus(s)} className={`px-3 h-[30px] rounded-lg text-[12px] font-medium transition-all cursor-pointer whitespace-nowrap ${status === s ? 'bg-white text-champagne-600 shadow-sm' : 'text-charcoal-400 hover:text-charcoal-600'}`}>{s}</button>
            ))}
          </div>
          {/* Toggle filters */}
          {[
            { label: 'Popüler', val: filterPopular, set: setFilterPopular },
            { label: 'Trend', val: filterTrending, set: setFilterTrending },
            { label: 'Yeni', val: filterNew, set: setFilterNew },
          ].map(f => (
            <button key={f.label} onClick={() => f.set(!f.val)} className={`h-[38px] px-3.5 rounded-xl text-[12px] font-medium border transition-all cursor-pointer whitespace-nowrap ${f.val ? 'bg-champagne-500 text-white border-champagne-500' : 'bg-[#FAF8F4] text-charcoal-400 border-border hover:border-champagne-300'}`}>{f.label}</button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            {/* View toggle */}
            <div className="flex bg-[#FAF8F4] rounded-xl p-1 gap-1">
              <button onClick={() => setViewMode('table')} className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all cursor-pointer ${viewMode === 'table' ? 'bg-white shadow-sm text-champagne-600' : 'text-charcoal-400'}`}><i className="ri-list-check text-[15px]" /></button>
              <button onClick={() => setViewMode('grid')} className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all cursor-pointer ${viewMode === 'grid' ? 'bg-white shadow-sm text-champagne-600' : 'text-charcoal-400'}`}><i className="ri-grid-line text-[15px]" /></button>
            </div>
            <button className="flex items-center gap-2 h-[38px] px-4 bg-[#FAF8F4] border border-border rounded-xl text-[13px] text-charcoal-500 hover:border-champagne-300 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-download-line text-[14px]" /> Dışa Aktar
            </button>
            <button className="flex items-center gap-2 h-[38px] px-4 rounded-xl text-[13px] font-medium text-white cursor-pointer whitespace-nowrap shadow-gold" style={{ background: 'linear-gradient(135deg, #D4AF37, #E8C87C)' }}>
              <i className="ri-add-line text-[14px]" /> Kampanya Ekle
            </button>
          </div>
        </div>
      </div>

      {/* Table View */}
      {viewMode === 'table' ? (
        <div className="bg-white rounded-xl2 shadow-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-[#FAF8F4] border-b border-border">
                <th className="w-10 px-4 py-3"><input type="checkbox" className="rounded" /></th>
                <th className="px-4 py-3 text-left text-[12px] font-semibold text-charcoal-400 whitespace-nowrap">GÖRSEL</th>
                <th className="px-4 py-3 text-left text-[12px] font-semibold text-charcoal-400 whitespace-nowrap">KAMPANYA</th>
                <th className="px-4 py-3 text-left text-[12px] font-semibold text-charcoal-400 whitespace-nowrap">KATEGORİ</th>
                <th className="px-4 py-3 text-left text-[12px] font-semibold text-charcoal-400 whitespace-nowrap">FİYATLANDIRMA</th>
                <th className="px-4 py-3 text-left text-[12px] font-semibold text-charcoal-400 whitespace-nowrap">BAŞVURU</th>
                <th className="px-4 py-3 text-left text-[12px] font-semibold text-charcoal-400 whitespace-nowrap">DURUM</th>
                <th className="px-4 py-3 text-left text-[12px] font-semibold text-charcoal-400 whitespace-nowrap">ETİKET</th>
                <th className="px-4 py-3 text-left text-[12px] font-semibold text-charcoal-400 whitespace-nowrap">İŞLEMLER</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id} className="border-b border-border hover:bg-blush-50 transition-colors">
                  <td className="px-4 py-3"><input type="checkbox" className="rounded" /></td>
                  <td className="px-4 py-3">
                    <div className="w-[72px] h-[52px] rounded-xl overflow-hidden bg-[#FAF8F4] flex-shrink-0">
                      <img src={c.coverImage} alt={c.title} className="w-full h-full object-cover object-top" />
                    </div>
                  </td>
                  <td className="px-4 py-3 max-w-[180px]">
                    <p className="text-[14px] font-semibold text-charcoal-600 truncate">{c.title}</p>
                    <p className="text-[11px] text-charcoal-300 font-mono truncate">/{c.slug}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[11px] font-medium px-2.5 py-1 bg-champagne-50 text-champagne-700 rounded-full whitespace-nowrap">{c.category}</span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-[14px] font-bold text-champagne-600">₺{c.newPrice.toLocaleString('tr-TR')}</p>
                    <p className="text-[11px] text-charcoal-300 line-through">₺{c.oldPrice.toLocaleString('tr-TR')}</p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-user-line text-charcoal-300 text-[13px]" /></div>
                      <span className="text-[13px] font-semibold text-charcoal-600">{c.applicantCount}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${statusColors[c.status]}`}>
                      {c.status === 'active' ? '● Aktif' : '● Pasif'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {c.popular && <CampaignBadge label="Popüler" color="bg-champagne-50 text-champagne-700" />}
                      {c.trending && <CampaignBadge label="Trend" color="bg-pink-50 text-pink-600" />}
                      {c.isNew && <CampaignBadge label="Yeni" color="bg-green-50 text-green-600" />}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <ActionBtn icon="ri-eye-line" color="text-charcoal-400 hover:text-champagne-600 hover:bg-champagne-50" />
                      <ActionBtn icon="ri-edit-line" color="text-charcoal-400 hover:text-blue-500 hover:bg-blue-50" />
                      <ActionBtn icon="ri-file-copy-line" color="text-charcoal-400 hover:text-green-600 hover:bg-green-50" />
                      <ActionBtn icon="ri-delete-bin-line" color="text-charcoal-400 hover:text-red-500 hover:bg-red-50" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-5 py-4 flex items-center justify-between border-t border-border">
            <span className="text-[12px] text-charcoal-400">Toplam <strong className="text-charcoal-600">{filtered.length}</strong> kampanya</span>
            <div className="flex items-center gap-1">
              {[1, 2, 3].map(p => (
                <button key={p} className={`w-8 h-8 flex items-center justify-center rounded-lg text-[12px] font-medium transition-colors cursor-pointer ${p === 1 ? 'bg-champagne-500 text-white' : 'text-charcoal-400 hover:bg-[#FAF8F4]'}`}>{p}</button>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.map(c => <CampaignCard key={c.id} campaign={c} />)}
        </div>
      )}
    </Layout>
  );
}

function ActionBtn({ icon, color }: { icon: string; color: string }) {
  return (
    <button className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all cursor-pointer ${color}`}>
      <i className={`${icon} text-[14px]`} />
    </button>
  );
}

function CampaignCard({ campaign: c }: { campaign: Campaign }) {
  return (
    <div className="bg-white rounded-xl2 shadow-card overflow-hidden hover:shadow-hover transition-shadow group">
      <div className="relative w-full h-40">
        <img src={c.coverImage} alt={c.title} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
        <div className="absolute top-3 left-3 flex gap-1 flex-wrap">
          {c.popular && <span className="text-[10px] font-semibold px-2 py-0.5 bg-champagne-500 text-white rounded-full">Popüler</span>}
          {c.trending && <span className="text-[10px] font-semibold px-2 py-0.5 bg-pink-500 text-white rounded-full">Trend</span>}
          {c.isNew && <span className="text-[10px] font-semibold px-2 py-0.5 bg-green-500 text-white rounded-full">Yeni</span>}
        </div>
        <div className="absolute top-3 right-3 text-[11px] font-bold px-2 py-0.5 bg-red-500 text-white rounded-full">%{c.discount}</div>
      </div>
      <div className="p-4">
        <span className="text-[10px] font-medium px-2 py-0.5 bg-champagne-50 text-champagne-700 rounded-full">{c.category}</span>
        <h4 className="text-[14px] font-semibold text-charcoal-600 mt-2 leading-tight line-clamp-2">{c.title}</h4>
        <div className="flex items-center gap-2 mt-2">
          <span className="text-[16px] font-bold text-champagne-600">₺{c.newPrice.toLocaleString('tr-TR')}</span>
          <span className="text-[12px] text-charcoal-300 line-through">₺{c.oldPrice.toLocaleString('tr-TR')}</span>
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
          <div className="flex items-center gap-1 text-[11px] text-charcoal-400">
            <i className="ri-user-line" /> {c.applicantCount} başvuru
          </div>
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusColors[c.status]}`}>
            {c.status === 'active' ? 'Aktif' : 'Pasif'}
          </span>
        </div>
        <div className="flex items-center gap-1.5 mt-3">
          <button className="flex-1 h-8 flex items-center justify-center gap-1 text-[12px] text-charcoal-500 bg-[#FAF8F4] rounded-lg hover:bg-champagne-50 hover:text-champagne-600 transition-colors cursor-pointer"><i className="ri-eye-line text-[13px]" /> Gör</button>
          <button className="flex-1 h-8 flex items-center justify-center gap-1 text-[12px] text-charcoal-500 bg-[#FAF8F4] rounded-lg hover:bg-blue-50 hover:text-blue-500 transition-colors cursor-pointer"><i className="ri-edit-line text-[13px]" /> Düzenle</button>
        </div>
      </div>
    </div>
  );
}
